import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/context/AuthContext'
import { useStorage } from '@/hooks/useStorage'
import { useAuthApi } from '@/services/api/authApi'
import { useAuthStore } from '@/store/authStore'
import { useUTMParams } from '@/hooks/useUTMParams'

import { ApiResponse } from '@/types/api'
import type {
    AuthResponse,
    LoginRequest,
    RegisterRequest,
    UserBasic
} from '@/types/auth'
interface AuthHookProps {
    user: UserBasic | null
    idAspirant: number | null
    idInscription: number | null
    accessToken: string | null
    refreshToken: string | null
    isAuthenticated: boolean
    firstName: string | null
    lastName: string | null
    email: string | null
    cellphone: string | null
    isAgent: boolean
    idAsesor: number | null
    login: (credentials: LoginRequest) => Promise<ApiResponse<AuthResponse>>
    register: (data: RegisterRequest) => Promise<ApiResponse<AuthResponse>>
    logout: () => Promise<void>
}
export function useAuthHook(): AuthHookProps {
    const navigate = useNavigate()
    const authApi = useAuthApi()
    const { navigateWithUTM } = useUTMParams()
    const { login: authLogin, logout: authLogout } = useAuth()
    const { clearAuthStorage } = useStorage()
    const {
        user,
        idAspirant,
        accessToken,
        refreshToken,
        isAuthenticated,
        idInscription,
        firstName,
        lastName,
        email,
        cellphone,
        isAgent,
        idAsesor
    } = useAuthStore()

    const login = useCallback(
        async (credentials: LoginRequest) => {
            // First ensure we clear any existing conflicting storage
            clearAuthStorage()

            let response

            try {
                if (credentials.isAgentLogin) {
                    response = await authApi.agentLogin(
                        credentials.tokenAccess!,
                        credentials.password!,
                        credentials.idAsesor!
                    )
                } else {
                    // Standard email/password login
                    response = await authApi.login(credentials)
                }

                // Check if the login was successful (status === 200) before proceeding
                if (response.status === 200) {
                    // For agent login, pass isAgentAccess=true to use sessionStorage
                    if (credentials.isAgentLogin) {
                        await authLogin(response.data, true)
                    } else {
                        // For Google or standard login, use default storage (localStorage)
                        await authLogin(response.data, false)
                    }

                    console.log(
                        'Login successful with status:',
                        response.status
                    )
                } else {
                    console.error('Login failed with status:', response.status)
                    // Don't authenticate user if status is not 200
                }

                return response
            } catch (error) {
                console.error('Login error:', error)
                throw error
            }
        },
        [authApi, authLogin, clearAuthStorage]
    )

    const register = useCallback(
        async (data: RegisterRequest) => {
            // Clear any existing auth storage first to prevent conflicts
            clearAuthStorage()

            const response = await authApi.register(data)

            // Only proceed with login if registration was successful
            if (response.status === 200) {
                // Register always creates a regular aspirant account, not an agent
                await authLogin(response.data, false)
            }

            return response
        },
        [authApi, authLogin, clearAuthStorage]
    )

    const logout = useCallback(async () => {
        try {
            if (refreshToken) {
                await authApi.logout(refreshToken)
            } else {
                console.log('No refresh token found')
            }
        } finally {
            // This will call clearAuthStorage internally
            authLogout()
            navigateWithUTM('/login')
        }
    }, [authLogout, navigateWithUTM, refreshToken, authApi])

    return {
        user,
        idAspirant,
        idInscription,
        accessToken,
        refreshToken,
        isAuthenticated,
        firstName,
        lastName,
        email,
        cellphone,
        isAgent,
        idAsesor,
        login,
        register,
        logout
    }
}
